import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import {
  updateCache,
  useKeyValueSettingsMutation
} from '~/modules/common/hooks';
import { useMeContext } from '~/modules/me/useMeContext';
import {
  resourcingLegendItems,
  resourcingLegendItemsV2
} from '~/modules/resourcing/common/components/ResourcingLegends/resourcingLegendItems';
import {
  DEFAULT_DISPLAY_UNIT,
  DEFAULT_PERIOD_SCALE
} from '~/modules/resourcing/common/enums';
import { ResourceAllocationStatus, ResourceRequestStatus } from '~/types';
import { useGetChartDates } from './useGetResourceChartInformation';

export const ResourcingChartToolbarContext = createContext();

const requestStatusList = [
  resourcingLegendItems.request,
  resourcingLegendItems.requestRejected,
  resourcingLegendItems.toBeHired,
  resourcingLegendItems.resourceRejected,
  resourcingLegendItems.pending,
  resourcingLegendItems.proposed,
  resourcingLegendItems.complete
];

const getRequestStatusListV2 = ({
  isResourceActualModeEnabled,
  isPsaRmpTaskAllocation1Enabled
}) => ({
  none: [resourcingLegendItemsV2.notWorking],
  resourceRequests: [
    resourcingLegendItemsV2.request,
    resourcingLegendItemsV2.requestRejected,
    resourcingLegendItemsV2.toBeHired,
    resourcingLegendItemsV2.resourceRejected,
    resourcingLegendItemsV2.pending,
    resourcingLegendItemsV2.proposed
  ],
  resources: [
    resourcingLegendItemsV2.complete,
    isPsaRmpTaskAllocation1Enabled && resourcingLegendItemsV2.overAllocated,
    isResourceActualModeEnabled && resourcingLegendItemsV2.actuals
  ].filter(ele => ele)
});

const requestStatusMap = {
  [ResourceRequestStatus.Draft]: resourcingLegendItems.request,
  [ResourceRequestStatus.Rejected]: resourcingLegendItems.requestRejected,
  [ResourceRequestStatus.Tobehired]: resourcingLegendItems.toBeHired,
  [ResourceRequestStatus.Allocationrejected]:
    resourcingLegendItems.resourceRejected,
  [ResourceRequestStatus.Submitted]: resourcingLegendItems.pending,
  [ResourceRequestStatus.Tentative]: resourcingLegendItems.proposed,
  [ResourceRequestStatus.Complete]: resourcingLegendItems.complete
};

const allocationToRequestMap = {
  [ResourceAllocationStatus.Committed]: [ResourceRequestStatus.Complete],
  [ResourceAllocationStatus.Rejected]: [ResourceRequestStatus.Rejected],
  [ResourceAllocationStatus.Proposed]: [ResourceRequestStatus.Tentative],
  [ResourceAllocationStatus.Draft]: [ResourceRequestStatus.Draft]
};

const useResourcingChartToolbarContext = () =>
  useContext(ResourcingChartToolbarContext);

export const getLegends = ({
  requestStatusesFromPlan = [],
  allocationStatusesFromPlan = []
}) => {
  const allocationLegendsMapped = allocationStatusesFromPlan.flatMap(
    status => allocationToRequestMap[status]
  );

  const legendsSet = new Set([
    ...requestStatusesFromPlan,
    ...allocationLegendsMapped
  ]);

  const legends = Array.from(legendsSet).map(
    status => requestStatusMap[status]
  );

  const legendsInOrder = requestStatusList.filter(legend =>
    legends.includes(legend)
  );

  const includeNotWorkingLegend = [
    ResourceAllocationStatus.Committed,
    ResourceAllocationStatus.Proposed,
    ResourceAllocationStatus.Rejected
  ].some(status => allocationStatusesFromPlan.includes(status));

  return includeNotWorkingLegend
    ? [...legendsInOrder, resourcingLegendItems.notWorking]
    : legendsInOrder;
};

export const useResourcingChartToolbarContextValue = ({
  width,
  setSearchCriteria,
  searchCriteria,
  filter,
  sort,
  onSortChange,
  resourceRequestsMinMaxDateAndStatuses,
  resourceAllocationsMinMaxDateAndStatuses,
  chartSettings,
  project
}) => {
  const [scale, setScale] = useState(
    chartSettings?.scale || DEFAULT_PERIOD_SCALE
  );

  const [displayUnit, setDisplayUnit] = useState(
    chartSettings?.displayUnit || DEFAULT_DISPLAY_UNIT
  );

  const [
    isResourceActualModeEnabled,
    setIsResourceActualModeEnabled
  ] = useState(chartSettings?.showActuals || false);

  const {
    featureFlags: {
      isPsaRmpTaskAllocation1Enabled,
      isRmp2025Q3Enhancements,
      isRmpRemoveByDateEsIndexEnabled,
      isRmp2025Q4Enhancements
    }
  } = useMeContext();
  const showNewLegends =
    isRmpRemoveByDateEsIndexEnabled ||
    isPsaRmpTaskAllocation1Enabled ||
    isRmp2025Q3Enhancements;

  const [putKeyValueSettings] = useKeyValueSettingsMutation(
    updateCache('resource_plan_chart_settings')
  );

  const handleSettingsChange = useCallback(
    ({
      displayUnit: newDisplayUnit,
      showActuals: newIsResourceActualModeEnabled,
      scale: newScale
    }) => {
      putKeyValueSettings({
        variables: {
          input: {
            key: 'resource_plan_chart_settings',
            settings: {
              displayUnit: newDisplayUnit,
              showActuals: newIsResourceActualModeEnabled,
              scale: newScale
            }
          }
        }
      });
    },
    [putKeyValueSettings]
  );

  const legends = showNewLegends
    ? getRequestStatusListV2({
        isResourceActualModeEnabled,
        isPsaRmpTaskAllocation1Enabled
      })
    : getLegends({
        requestStatusesFromPlan:
          resourceRequestsMinMaxDateAndStatuses?.requestStatuses,
        allocationStatusesFromPlan:
          resourceAllocationsMinMaxDateAndStatuses?.allocationStatuses
      });

  const {
    chartDates,
    onPrevious,
    onNext,
    dateRange,
    setFixedRangeScale,
    setDate
  } = useGetChartDates({
    width,
    scale,
    resourceRequestsMinMaxDateAndStatuses,
    resourceAllocationsMinMaxDateAndStatuses,
    sessionStorageKey: isRmp2025Q3Enhancements
      ? `resource-plan-chart-start-date-${project.id}`
      : undefined,
    isRmp2025Q3Enhancements
  });

  const onScaleChange = useCallback(
    newValue => {
      setScale(newValue);
      setFixedRangeScale(newValue);
      handleSettingsChange({
        displayUnit,
        showActuals: isResourceActualModeEnabled,
        scale: newValue
      });
    },
    [
      displayUnit,
      handleSettingsChange,
      isResourceActualModeEnabled,
      setFixedRangeScale
    ]
  );

  const onSetDisplayUnit = useCallback(
    newValue => {
      setDisplayUnit(newValue);
      handleSettingsChange({
        displayUnit: newValue,
        showActuals: isResourceActualModeEnabled,
        scale
      });
    },
    [handleSettingsChange, isResourceActualModeEnabled, scale]
  );

  const onSetIsResourceActualModeEnabled = useCallback(
    newValue => {
      setIsResourceActualModeEnabled(newValue);
      handleSettingsChange({
        displayUnit,
        showActuals: newValue,
        scale
      });
    },
    [displayUnit, handleSettingsChange, scale]
  );

  return useMemo(
    () => ({
      displayUnit,
      setDisplayUnit: onSetDisplayUnit,
      scale,
      chartDates,
      dateRange,
      setScale: onScaleChange,
      onPrevious,
      onNext,
      isResourceActualModeEnabled,
      setIsResourceActualModeEnabled: onSetIsResourceActualModeEnabled,
      legends,
      setSearchCriteria,
      searchCriteria,
      filter,
      sort,
      onSortChange,
      handleSettingsChange,
      setDate,
      isRmp2025Q4Enhancements
    }),
    [
      displayUnit,
      onSetDisplayUnit,
      scale,
      chartDates,
      dateRange,
      onScaleChange,
      onPrevious,
      onNext,
      isResourceActualModeEnabled,
      onSetIsResourceActualModeEnabled,
      legends,
      setSearchCriteria,
      searchCriteria,
      filter,
      sort,
      onSortChange,
      handleSettingsChange,
      setDate,
      isRmp2025Q4Enhancements
    ]
  );
};

export default useResourcingChartToolbarContext;
